import { useState } from 'react';
import {
  Card, CardContent,
} from '@eriko/ui/src/components/ui/card';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@eriko/ui/src/components/ui/dialog';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  useCisSubcontractors,
  useCreateCisSubcontractor,
  useDeleteCisSubcontractor,
} from '../../../../hooks/useUkTax';

function formatDeductionRate(bps: number | null): string {
  if (bps === null) return 'Unverified';
  if (bps === 0) return 'Gross (0%)';
  return `${bps / 100}%`;
}

function formatPence(minor: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(minor / 100);
}

function AddSubcontractorDialog({ onClose }: { onClose: () => void }) {
  const createSubcontractor = useCreateCisSubcontractor();
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [utr, setUtr] = useState('');

  function handleSubmit() {
    createSubcontractor.mutate(
      {
        legalName,
        tradingName: tradingName || undefined,
        utr: utr || undefined,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Subcontractor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="legalName">Legal Name *</Label>
            <Input
              id="legalName"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Full legal name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tradingName">Trading Name</Label>
            <Input
              id="tradingName"
              value={tradingName}
              onChange={(e) => setTradingName(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="utr">UTR (Unique Taxpayer Reference)</Label>
            <Input
              id="utr"
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              placeholder="10-digit UTR (optional)"
            />
          </div>
          {createSubcontractor.isError && (
            <p className="text-sm text-red-500">Failed to add subcontractor. Please try again.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!legalName || createSubcontractor.isPending}
          >
            {createSubcontractor.isPending ? 'Adding...' : 'Add subcontractor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CisSubTab() {
  const { data: subcontractors, isLoading } = useCisSubcontractors();
  const deleteSubcontractor = useDeleteCisSubcontractor();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-surface-700">
          CIS Subcontractors ({subcontractors?.length ?? 0})
        </h3>
        <Button size="sm" onClick={() => setShowAddDialog(true)}>
          Add subcontractor
        </Button>
      </div>

      {(!subcontractors || subcontractors.length === 0) ? (
        <Card>
          <CardContent className="py-8 text-center text-surface-500 text-sm">
            No subcontractors yet. Add one to track CIS payments.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {[...subcontractors].sort((a, b) => a.legalName.localeCompare(b.legalName)).map((sc) => (
            <Card key={sc.id}>
              <CardContent className="flex items-center justify-between py-3 px-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-surface-900">
                      {sc.legalName}
                    </span>
                    {sc.tradingName && (
                      <span className="text-xs text-surface-500">({sc.tradingName})</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {sc.utrLast4 ? (
                      <span className="text-xs text-surface-500">
                        UTR: ******{sc.utrLast4}
                      </span>
                    ) : (
                      <span className="text-xs text-surface-400">No UTR</span>
                    )}
                    {sc.cisVerifiedAt ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        Verified
                      </Badge>
                    ) : (
                      <>
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          Unverified
                        </Badge>
                        <span className="text-xs text-muted-foreground ml-1">— ask your bookkeeper to verify with HMRC</span>
                      </>
                    )}
                    <span className="text-xs text-surface-500">
                      Rate: {formatDeductionRate(sc.cisDeductionRate)} <span className="text-xs text-muted-foreground">(HMRC deduction rate)</span>
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-surface-500">
                    <span>YTD Payments: {formatPence(sc.ytdPaymentsMinor)}</span>
                    <span>YTD Deductions: {formatPence(sc.ytdDeductionsMinor)}</span>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  {confirmDeleteId === sc.id ? (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={deleteSubcontractor.isPending}
                          onClick={() => {
                            deleteSubcontractor.mutate(sc.id, {
                              onSettled: () => setConfirmDeleteId(null),
                            });
                          }}
                        >
                          {deleteSubcontractor.isPending ? 'Removing...' : 'Confirm'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                      {deleteSubcontractor.isError && (
                        <p className="text-xs text-red-500">Failed to remove. Try again.</p>
                      )}
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-surface-400 hover:text-red-600"
                      onClick={() => setConfirmDeleteId(sc.id)}
                      aria-label="Delete subcontractor"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showAddDialog && (
        <AddSubcontractorDialog onClose={() => setShowAddDialog(false)} />
      )}
    </div>
  );
}
